import { Flex, Image, Modal, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDownload, IconHomeFilled, IconShare2, IconSquarePlus } from '@tabler/icons-react';
import React, { useState, useEffect } from 'react';
import colors from '../utils/colors';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt: () => Promise<void>;
}

const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallButton, setShowInstallButton] = useState<boolean>(false);

  const isInStandaloneMode = (): boolean =>
    window.matchMedia('(display-mode: standalone)').matches ||
    // Dla Safari na iOS:
    (window.navigator as any).standalone === true;
  
  const isIOS = (): boolean => {
    const userAgent: string = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  };

  useEffect(() => {

    if (isInStandaloneMode()) {
      setShowInstallButton(false);
      return;
    }

    if (isIOS()) {
      setShowInstallButton(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      promptEvent.prompt().then(() => {
        promptEvent.userChoice.then(choiceResult => {
          if (choiceResult.outcome === 'dismissed') {
            setShowInstallButton(true);
          }
        });
      }).catch(() => {
        setShowInstallButton(true);
      });
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const [opened, { open, close }] = useDisclosure(false);
  
  const handleInstallClick = async () => {

    if (isIOS()) {
      open()
      //window.open('https://support.apple.com/guide/iphone/bookmark-a-website-iph42ab2f3a7/ios', '_blank');
      return;
    }

    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);
    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  if (!showInstallButton) {
    return null;
  }

  return (
    <>
        <button 
        className="text-white fixed top-3 bg-gradient-to-br from-custom36p via-custom36s to-custom36s hover:bg-gradient-to-r focus:ring-4 focus:outline-none focus:ring-custom36p shadow-lg shadow-custom36p/50 font-medium rounded-lg border-none text-sm px-7 py-2 text-center tracking-wide"
        onClick={handleInstallClick}>
          <div className="flex items-center justify-center">     
            {isIOS() ? <IconHomeFilled className="mr-2" /> : <IconDownload className="mr-2" />}
            {isIOS() ? 'INSTALACJA NA IOS' : 'ZAINSTALUJ APLIKACJĘ'}
          </div>
        </button>
        {/* MODAL DLA IOS */}
        <Modal opened={opened} onClose={close} centered withCloseButton={false} size="lg" yOffset="1vh" radius={'lg'}
          transitionProps={{ 
            transition: 'fade', 
            duration: 200, 
            timingFunction: 'linear' 
          }} 
          overlayProps={{
            backgroundOpacity: 0.55,
            blur: 3,
          }}
          styles={{body: {background: colors.customBg3}}}
          >
          <Flex c={colors.white} justify={'center'} direction={'column'} align={'center'} gap={'lg'}>
            <Flex direction={'column'} gap='2' align={'center'}>
              <Text c={colors.custom36s} ta="center" fw={700} size='lg' component="span" style={{ display: 'inline-flex', alignItems: 'center' }}> <IconHomeFilled style={{ marginRight: 4, verticalAlign: 'middle', marginTop: -3 }} size={24}/>Instalacja na iOS</Text>
              <Text c={colors.custom36s} ta="center" fw={500} size='md'>Wymagane jest użycie przeglądarki Safari</Text>
            </Flex>
            <Flex direction={'column'} gap='xs' align={'center'}>
              <Text c={colors.custom36s} ta="center" fw={700} size='sm'>
                Krok 1: Kliknij w ikonkę <Text component="span" fw={700} c="#0058dd" style={{ display: 'inline-flex', alignItems: 'center' }}>Udostępnij <IconShare2 style={{ marginLeft: 4, verticalAlign: 'middle' }} size={18} /></Text> znajdującą się na dole ekranu.
              </Text>
              <Image src={'/ios/ios1.png'} 
                radius="md"
                w={'80vw'}
                mah={100}
                maw={'22rem'}
                fit={'contain'}
                />
            </Flex>
            <Flex direction={'column'} gap='xs' align={'center'}>
              <Text c={colors.custom36s} ta="center" fw={700} size='sm'>
                Krok 2: Wybierz opcję <Text component="span" fw={700} c="#0058dd" style={{ display: 'inline-flex', alignItems: 'center' }}>Do ekranu głównego <IconSquarePlus style={{ marginLeft: 4, verticalAlign: 'middle' }} size={18} /></Text>
              </Text>
              <Image src={'/ios/ios2ver2.png'} 
                radius="md"
                w={'80vw'}
                mah={'16rem'}
                fit={'contain'}
                />
            </Flex>
            <Flex direction={'column'} gap='xs' align={'center'}>
              <Text c={colors.custom36s} ta="center" fw={700} size='sm'>
                Krok 3: Kliknij w przycisk <Text component="span" fw={700} c="#0058dd">Dodaj</Text> w prawym górnym rogu ekranu.
              </Text>
              <Image src={'/ios/ios3.png'} 
                radius="md"
                w={'80vw'}
                mah={'11rem'}
                fit={'contain'}
                />
            </Flex>
            <Flex direction={'column'} gap='xs' align={'center'}>
              <Text c={colors.custom36s} ta="center" fw={700} size='sm'>
                Krok 4: Włącz aplikację <Text component="span" fw={700} c="#0058dd">36 MINUT</Text> z ekranu głównego swojego telefonu.
              </Text>
            </Flex>
          </Flex>
        </Modal>
    </>
  );
};

export default InstallPrompt;
